import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import { Badge, Popover } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { ListagemArquivosActions } from 'Store/ListagemArquivos/ListagemArquivos.actions';
import { ApisEnumLabel, getIconApi } from 'Data/enums/Apis';
import PageHeader from 'Components/UI/PageHeader/PageHeader';
import Table from 'Components/UI/Table';
import BreadCrumb, { IBreadcrumbItem } from 'Components/UI/BreadCrumb';
import TableActionTopbar, {
  ITableActionTopbarButton,
} from 'Components/UI/TableActionTopbar';
import { Icon, IconName } from 'Components/UI';
import ColumnHeader from 'Components/UI/Table/ColumnHeader';
import CadastroDisciplina from './Modals/CadastroDisciplina';
import ExclusaoDisciplinas from './Modals/ExclusaoDisciplina';
import { saveOrderList } from 'Pages/ListagemArquivos/ListagemArquivos.helpers';
import toastHandler from 'Utils/toastHandler';
import AccessPermission from 'Utils/AcessPermission';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import LoadingDisciplinas from 'Components/UI/CustomLoading/LoadingDisciplinas';
import AppStorage from 'Utils/AppStorage';
import TabsShell, { PathActiveEnum } from 'Components/UI/TabsShell';
import ModalBloquedSADownload from 'Components/UI/ModalBloquedSADownload';
import { ObraActions } from 'Store/Obra/Obra.actions';
import { Mixpanel } from 'Utils/MixPanel';
import TagIcon from 'Components/UI/TagIcon';
import ErrorSAConnection from './Modals/ErrorSAConnection';
import SolutionSAConnection from './Modals/SolutionSAConnection';
import { DisciplineFolderStatusEnum } from 'Data/interfaces/Obra/IDisciplineResponse';
import {
  INotificationInAppObra,
  INotificationMultipleReadRequest,
} from 'Data/interfaces/Notification/INotification';
import { getCurrentTenant, getTenants } from 'Store/Tenant/Tenant.selector';
import ReconnectIntegrationBox from 'Components/UI/ReconnectIntegrationBox';
import { Http } from 'Utils/Http';
import { useQueryUniqueObra } from 'Hooks/obras/useQueryUniqueObra';
import { OBRA_QUERY_KEY } from 'Data/keys/QueryKeys';
import { useQueryDeleteDiscipline } from 'Hooks/disciplines/useQueryDeleteDiscipline';
import { mapNotification } from 'Utils/ObraUtils';
import { useQueryInAppObraNotifications } from 'Hooks/notifications/useQueryInAppObraNotifications';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import { DotNotification } from 'Components/UI/Dot';
import { queryClient } from 'Context/QueryClientContext';
import { isObjectEmpty } from 'Utils/ObjectUtils';
import { useNotification } from 'Context/NotificationContext';
import MoreActionsDropdownContent from './components/MoreActionsDropdownContent';
import ContextMenu from 'Components/UI/ContextMenu';
import { IContextMenuRef } from 'Components/UI/ContextMenu/model';
import { useTimeout } from 'Context/TimeoutContext';
import { IListDisciplinesOrder, IListDisciplinesOrderKey } from 'Data/interfaces/Obra/ISortListDisciplines';
import { getOrderTitle } from './Disciplines.helper';
import { ActionColumn, DisciplineItem, TableWrapper } from './styles';
import { validateErrorMsg } from 'Store/Upload/Upload.sagas';
import { UploadActions } from 'Store/Upload/Upload.actions';
import SameAccountErrorModal from 'Components/UI/ReconnectIntegrationBox/SameAccountErrorModal';

export const RELOAD_DISCIPLINAS_KEY = 'RELOAD_DISCIPLINAS_KEY';

export interface IOpenMoreActionsDropdown {
  open: boolean;
  id: number | null;
}

const defaultOrder: IListDisciplinesOrder = {
  last: null,
  name: null,
};

const DisciplineIcons: IconName[] = [
  'disciplinaArquitetura',
  'disciplinaEstrutura',
  'disciplinaEletrica',
  'disciplinaHidrossanitaria',
  'disciplinaMecanica',
  'disciplinaFundacao',
  'disciplinaAr',
  'disciplinaIncendio',
  'menuContexto',
];

export enum DisciplineIconsEnum {
  'Arquitetura' = 'disciplinaArquitetura',
  'Estrutura' = 'disciplinaEstrutura',
  'Instalações Eletricas e Sistemas' = 'disciplinaEletrica',
  'Instalações Hidrossanitarias' = 'disciplinaHidrossanitaria',
  'Instalações Mecânicas' = 'disciplinaMecanica',
  'Fundações' = 'disciplinaFundacao',
  'Ar Condicionado' = 'disciplinaAr',
  'Incêndio' = 'disciplinaIncendio',
  'Engineer' = 'menuContexto',
  'Arquivos obsoletos' = 'obsoleteArchive',
}

const breadCrumbs: IBreadcrumbItem[] = [];

const Disciplinas: FC<Props> = (props) => {
  const { t } = useTranslation('disciplinas');
  const followDisciplineFeatureFlag = useFeatureFlag('follow-discipline-feature');
  const followDisciplineFeature = followDisciplineFeatureFlag.enabled && followDisciplineFeatureFlag.value === 'test';

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { set, clear } = useTimeout();
  const location = useLocation();
  const params = useParams();
  const { addNotificationsToRead } = useNotification();

  const [ignoreCache, setIgnoreCache] = useState(false);
  const hasCheckedReload = useRef(false);
  
  let reloadIgnoreCache = false;
  if (!hasCheckedReload.current) {
    if (!(window as any)[RELOAD_DISCIPLINAS_KEY]) {
      reloadIgnoreCache = true;
      (window as any)[RELOAD_DISCIPLINAS_KEY] = true;
    }
    hasCheckedReload.current = true;
  }

  const { data: obra, isFetching: isLoading, refetch } = useQueryUniqueObra({
    csId: Number(params.constructionSiteId),
    ignoreCache: ignoreCache || reloadIgnoreCache,
    navigate,
  });
  const {
    deleteDiscipline,
    isLoading: isDeletingDiscipline,
  } = useQueryDeleteDiscipline();
  const {
    data: inAppNotifications,
  } = useQueryInAppObraNotifications(Number(params.constructionSiteId));

  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);

  const firstOpen = useRef(0);
  const contextMenuRef = useRef<IContextMenuRef>(null);
  const [selectedDisciplinas, setSelectedDisciplinas] = useState<number[]>([]);
  const [showErrorSAConnectionInfo, setShowErrorSAConnectionInfo] = useState(false);
  const [showSolutionSAConnection, setShowSolutionSAConnection] = useState(false);
  const [disciplineForSolution, setDisciplineForSolution] = useState<any>(null);
  const [showCadastroDisciplina, setShowCadastroDisciplina] = useState(false);
  const [showModalExclusaoDisciplina, setShowModalExclusaoDisciplina] = useState(
    false,
  );
  const [editedDisciplineId, setEditedDisciplineId] = useState<number>();
  const [editedDisciplineName, setEditedDisciplineName] = useState<string>();
  const [editedDisciplineIcon, setEditedDisciplineIcon] = useState<string>(
    'menuContexto',
  );
  const [openMoreActionsDropdown, setOpenMoreActionsDropdown] = useState<IOpenMoreActionsDropdown>({
    open: false,
    id: null,
  });
  const [contextMenuRecord, setContextMenuRecord] = useState<any | null>(null);
  const [canReadNow, setCanReadNow] = useState(false);
  const [editedFolderId, setEditedFolderId] = useState<string>();
  const [order, setOrder] = useState<IListDisciplinesOrder>(defaultOrder);
  const [showSameAccountErrorModal, setShowSameAccountErrorModal] = useState(false);

  const notificationsObj = useMemo(() => {
    const foldersIds = [];
    const notificationsMap: Record<string, INotificationInAppObra[]> = {};
    for (const notification of inAppNotifications) {
      if (notification.ItemId in notificationsMap) {
        notificationsMap[notification.ItemId].push(notification);
      } else {
        notificationsMap[notification.ItemId] = [notification];
      }
      if (notification?.Folders?.length > 0) {
        foldersIds.push(...notification.Folders, notification.ItemId);
      }
    }
    return { foldersIds, notificationsMap };
  }, [inAppNotifications]);

  const sortedDisciplines = useMemo(() => {
    if (!!order.last && obra?.Disciplines?.length > 0) {
      if (order.last === 'name') {
        if (order.name === 'ascend') {
          return obra?.Disciplines?.sort((a: any, b: any) => {
            const propA = a?.CustomName || a?.Discipline?.Name || '';
            const propB = b?.CustomName || b?.Discipline?.Name || '';
            return propA.localeCompare(propB);
          });
        }
        return obra?.Disciplines?.sort((a: any, b: any) => {
          const propA = a?.CustomName || a?.Discipline?.Name || '';
          const propB = b?.CustomName || b?.Discipline?.Name || '';
          return propB.localeCompare(propA);
        });
      }
    }
    return obra?.Disciplines || [];
  }, [obra?.Disciplines, order]);

  const onChange = (selecteds: any) => setSelectedDisciplinas(selecteds);

  useEffect(() => {
    if (Http.GetQueryParams('SameAccountRequiredError') === 'true') {
      setShowSameAccountErrorModal(true);
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.delete('SameAccountRequiredError');
      navigate({
        pathname: window.location.pathname,
        search: queryParams.toString() ? `?${queryParams.toString()}` : '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      clear('canReadNow');
      clear('hasUploadErrorRedirect');
    }
  }, []);

  useEffect(() => {
    if (
      firstOpen.current === 0
      && obra && obra.ConstructionSiteId === Number(params.constructionSiteId)
    ) {
      if (params.isNewConstructionSite === 'true') {
        toastHandler.showSuccess(t('toast.obraCreated'));
        const queryParams = new URLSearchParams(location.search);
        queryParams.delete('isNewConstructionSite');
        navigate({
          pathname: location.pathname,
          search: queryParams.toString() ? `?${queryParams.toString()}` : '',
        });
      }
      Mixpanel.track({
        name: 'PAGE_VIEW',
        props: {
          constructionSiteId: Number(params.constructionSiteId),
        },
        userInfo: props.userInfo,
        currentListTenant: currentTenant,
        tenantIds: tenants && tenants.length > 0
          ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
          : undefined,
      });
      firstOpen.current++;
    }
  }, [obra]);

  useEffect(() => {
    if (firstOpen.current <= 0 && AppStorage.GetItem('listDisciplinesOrder')) {
      const listDisciplinesSaveOrder = AppStorage.GetItem('listDisciplinesOrder');
      if (listDisciplinesSaveOrder) {
        setOrder(JSON.parse(listDisciplinesSaveOrder) as IListDisciplinesOrder);
      }
    }
    if (firstOpen.current > 0 && !!order.last) {
      saveOrderList(
        'listDisciplinesOrder',
        order,
      );
    }
  }, [order]);

  useEffect(() => {
    if (!isLoading) {
      if (showCadastroDisciplina) {
        closeModalDiscipline();
      }
      setSelectedDisciplinas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    const pathname = location?.pathname || '';
    const search = location?.search;
    dispatch(ObraActions.storeArchivesPath({
      path: pathname,
      params,
      url: `${pathname}${search}`,
    }));
  }, [params, location, dispatch]);

  const editDiscipline = (id: number, name: string) => {
    setEditedDisciplineId(id);
    setEditedDisciplineName(name);
    setEditedFolderId(undefined);
    setShowCadastroDisciplina(true);
  };

  const newDiscipline = () => {
    setEditedDisciplineId(undefined);
    setEditedDisciplineName(undefined);
    setEditedFolderId(undefined);
    setShowCadastroDisciplina(true);
  };

  const closeModalDiscipline = () => {
    setShowCadastroDisciplina(false);
    setEditedDisciplineId(undefined);
    setEditedDisciplineName('');
    setEditedFolderId(undefined);
  };

  const handleOpenMoreActionsDropdown = (id: number, open: boolean) => {
    if (!followDisciplineFeature) { return; }

    setOpenMoreActionsDropdown({
      open,
      id,
    });
  };

  const handleCloseMoreActionsDropdown = () => {
    setOpenMoreActionsDropdown({
      open: false,
      id: null,
    });
  };

  const toggleFollowDiscipline = (disciplines: any[]) => {
    if (disciplines.length === 0) { return; }

    console.log('toggleFollowDiscipline', disciplines);
  };

  const openModalExcluirDisciplina = (
    id: number,
    name: string,
    icon: string,
    folderId: string,
  ) => {
    setEditedDisciplineId(id);
    setEditedDisciplineName(name);
    setEditedDisciplineIcon(icon);
    setEditedFolderId(folderId);
    handleCloseMoreActionsDropdown();
    setShowModalExclusaoDisciplina(true);
  };

  const removeFiles = () => {
    setShowModalExclusaoDisciplina(false);
    const request = {
      ConstructionSiteId: Number(params.constructionSiteId),
      ConstructionSiteDisciplineId: editedDisciplineId,
    };
    deleteDiscipline(request);
  };

  const openDisciplineOnCloud = () => {
    setShowModalExclusaoDisciplina(false);
    window.open(obra?.CloudServiceUrl, '_blank');
  };

  const forceListDisciplinesUpdate = () => {
    setIgnoreCache(true);
  };

  useEffect(() => {
    if (!ignoreCache) {
      return;
    }
    refetch().finally(() => setIgnoreCache(false));
  }, [ignoreCache]);

  const handleShowErrorSAConnectionInfo = () => {
    setShowErrorSAConnectionInfo(!showErrorSAConnectionInfo);
  };

  const handleSortListArchives = (key: IListDisciplinesOrderKey) => {
    if (!key) { return; }

    setOrder((prev) => {
      if (prev[key] === null) {
        return {
          ...defaultOrder,
          [key]: 'ascend',
          last: key,
        };
      }
      if (prev[key] === 'ascend') {
        return {
          ...defaultOrder,
          [key]: 'descend',
          last: key,
        };
      }
      return {
        ...defaultOrder,
        [key]: null,
        last: null,
      };
    });
  };

  const handleOpenSA = () => {
    if (!obra?.CloudServiceUrl) { return; }

    window.open(obra?.CloudServiceUrl, '_blank');

    Mixpanel.track({
      name: 'REDIRECT_SA_ACTION',
      props: {
        constructionSiteId: Number(params.constructionSiteId),
        api: obra?.Api,
      },
      userInfo: props.userInfo,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    });
  };

  const getActionsToolbar = (): ITableActionTopbarButton[] => {
    const selectedDisciplinasData = obra?.Disciplines?.filter((discipline: any) => {
      return selectedDisciplinas?.includes(discipline.ConstructionSiteDisciplinesId);
    });
    const hasFollowedDisciplines = selectedDisciplinasData?.some((discipline: any) => {
      return discipline?.IsFollow ?? false;
    });

    return [
      {
        icon: hasFollowedDisciplines ? 'notVisualizar' : 'visualizar',
        iconCustomSize: hasFollowedDisciplines ? 18 : 12,
        description: hasFollowedDisciplines
          ? (selectedDisciplinas.length > 1 ? t('toolbar.unfollowPlural') : t('toolbar.unfollow'))
          : (selectedDisciplinas.length > 1 ? t('toolbar.followPlural') : t('toolbar.follow')),
        hidden: !followDisciplineFeature || selectedDisciplinas.length === 0,
        action: () => {
          toggleFollowDiscipline(selectedDisciplinas);
        },
      },
      {
        icon: getIconApi(obra?.Api || 0),
        description: t('toolbar.viewIn', { api: ApisEnumLabel[obra?.Api || 0] }),
        hidden: !AccessPermission.downloadFiles(props.currentUserPrivileges) ||
          AccessPermission.isEngOrConsult() ||
          !obra?.IsIntegrationAvailable,
        isBlocked: !obra?.CloudServiceUrl,
        action: () => handleOpenSA(),
        dropdownMenu: obra?.CloudServiceUrl
          ? undefined
          : (toggleDropdown: () => void) => (
            <ModalBloquedSADownload
              platform={obra?.Api || 0}
              onCancel={() => toggleDropdown()}
            />
          ),
      },
      {
        icon: 'atualizar',
        description: t('toolbar.refresh'),
        hidden: selectedDisciplinas.length > 0 || !obra?.IsIntegrationAvailable,
        action: () => forceListDisciplinesUpdate(),
      },
      {
        icon: 'arquivar',
        description: t('toolbar.newDiscipline'),
        hidden: selectedDisciplinas.length > 0 ||
          !AccessPermission.newOrEditDiscipline(props.currentUserPrivileges) ||
          !obra?.IsIntegrationAvailable,
        action: () => newDiscipline(),
      },
      {
        icon: 'editar',
        description: t('toolbar.edit'),
        hidden:
          selectedDisciplinas.length !== 1 || !AccessPermission.newOrEditDiscipline(props.currentUserPrivileges),
        action: () => {
          const index = selectedDisciplinas[0];
          const discipline = obra?.Disciplines[index];
          editDiscipline(
            discipline.ConstructionSiteDisciplinesId,
            discipline.CustomName || discipline.Discipline.Name,
          );
        },
      },
      {
        icon: 'excluir',
        hidden: selectedDisciplinas.length !== 1 || !AccessPermission.deleteDiscipline(),
        action: () => {
          const index = selectedDisciplinas[0];
          const discipline = obra?.Disciplines[index];
          openModalExcluirDisciplina(
            discipline.ConstructionSiteDisciplinesId,
            discipline.CustomName || discipline.Discipline.Name,
            discipline.Discipline.Icon,
            discipline.DisciplineApiFolderId,
          );
        },
      },
    ];
  };

  const sortDisciplinesList = (sortOrder: boolean) => {
    saveOrderList('listDisciplinesOrder', order);

    const currentSortedDisciplines = [
      ...(obra?.Disciplines.sort((a: any, b: any) => {
        const propA = a['CustomName'] || a['Discipline']['Name'] || '';
        const propB = b['CustomName'] || b['Discipline']['Name'] || '';

        if (typeof propA === 'number' && typeof propB === 'number') {
          return sortOrder ? propB - propA : propA - propB;
        }

        return propA.toString().localeCompare(propB.toString()) * (sortOrder ? -1 : 1);
      }) || []),
    ];

    queryClient.setQueryData<IObraResponse>(
      [OBRA_QUERY_KEY, Number(obra?.ConstructionSiteId)],
      (old) => {
        if (old) {
          return {
            ...old,
            Disciplines: currentSortedDisciplines,
          };
        }
        return old;
      },
    );
  };

  const handleSoluctionSAConnection = (discipline: any) => {
    setDisciplineForSolution(discipline);
    setShowSolutionSAConnection(true);
  };

  const closeSoluctionSAConnection = () => {
    setDisciplineForSolution(null);
    setShowSolutionSAConnection(false);
  };

  const menuId = `ContextMenuDisciplina`;

  const handleContextMenu = (e: any, record: any) => {
    setContextMenuRecord(record);
    contextMenuRef.current?.show(e);
  };

  const columnsTable: Array<ColumnProps<any>> = [
    {
      title: (
        <ColumnHeader
          title={t('table.columnName')}
          sorter={() => handleSortListArchives('name')}
          orderTooltip={getOrderTitle(order.name, 'name')}
        />
      ),
      dataIndex: 'CustomName',
      render: (text, record) => {
        const errorConnection = DisciplineFolderStatusEnum.NotFound === record.FolderStatus;
        const hasNotification = notificationsObj?.notificationsMap?.hasOwnProperty(record.DisciplineApiFolderId) ||
          notificationsObj?.notificationsMap?.hasOwnProperty(`${record.ConstructionSiteDisciplinesId}`) ||
          notificationsObj?.foldersIds?.some((folderID) => {
            return folderID === record.DisciplineApiFolderId || folderID === `${record.ConstructionSiteDisciplinesId}`;
          });

        return (
          <div onContextMenu={(e) => handleContextMenu(e, record)}>
            <DisciplineItem
              error={errorConnection}
              onClick={() =>
                errorConnection
                  ? handleShowErrorSAConnectionInfo()
                  : navigate(
                    `/obras/filelist/${record.ConstructionSiteFk}/${record.DisciplineApiFolderId}?obraApi=${obra?.Api}&folderName=${text ?? record.Discipline.Name}&ConstructionSiteDisciplinesId=${record.ConstructionSiteDisciplinesId}&DisciplineName=${record.CustomName || record.Discipline.Name}`,
                  )
              }
            >
              <ErrorSAConnection
                platform={obra?.Api || 0}
                userRole={props.userInfo?.CurrentRoleFk}
                onAction={() => handleSoluctionSAConnection(record)}
                triggered={showSolutionSAConnection || !errorConnection}
              >
                <Icon
                  error={errorConnection}
                  color='cinzaPadrao'
                  icon={DisciplineIcons[record.Discipline.DisciplineId - 1] || 'menuContexto'}
                  customSize={20}
                />
                <span className='disciplineItemText'>
                  {text ?? record.Discipline.Name}
                </span>
                {hasNotification && (
                  <span className="dotWrapper"><DotNotification /></span>
                )}
                {errorConnection ? (
                  <TagIcon
                    text={t('table.connectionError')}
                    icon={<Icon icon='aviso' color='branco' customSize={12} />}
                    error={errorConnection}
                    onClick={handleShowErrorSAConnectionInfo}
                  />
                ) : null}
              </ErrorSAConnection>
            </DisciplineItem>
          </div>
        );
      },
    },
    {
      title: (
        followDisciplineFeature ? (
          <ColumnHeader
            title={''}
            showOrder={false}
          />
        ) : null
      ),
      width: followDisciplineFeature ? undefined : '0%',
      className: 'columnAction',
      dataIndex: 'Action',
      render: (status: number, record: any) => {
        if (!followDisciplineFeature) { return null; }

        return (
          <ActionColumn>
            <Popover
              open={openMoreActionsDropdown.open &&
                openMoreActionsDropdown.id === record.ConstructionSiteDisciplinesId}
              trigger='click'
              placement='left'
              className='popoverMoreActions'
              content={
                <MoreActionsDropdownContent
                  preferencesFeature={followDisciplineFeature}
                  record={record}
                  apiText={t('toolbar.viewIn', { api: ApisEnumLabel[obra?.Api || 0] })}
                  onToggleFollow={toggleFollowDiscipline}
                  onEdit={editDiscipline}
                  onOpenSA={handleOpenSA}
                  onOpenHistoryDrawer={() => undefined}
                  onOpenModalExclusao={openModalExcluirDisciplina}
                />
              }
              onOpenChange={(open) => handleOpenMoreActionsDropdown(
                record.ConstructionSiteDisciplinesId,
                open,
              )}
              overlayInnerStyle={{ padding: 0 }}
            >
              <div className='iconWrapper'>
                <Icon icon='moreActions' className='iconAction' />
              </div>
            </Popover>
          </ActionColumn>
        );
      },
    },
  ];

  const rowSelection = {
    selectedDisciplinas,
    onChange,
  };

  useEffect(() => {
    dispatch(ListagemArquivosActions.setShowPlotRequestTable(false));
  }, [params.constructionSiteId]);

  useEffect(() => {
    if (
      !!props.checkUploadErrorMsg &&
      props.checkUploadErrorMsg.toLowerCase().includes(validateErrorMsg.toLowerCase())
    ) {
      clear('hasUploadErrorRedirect');
      set('hasUploadErrorRedirect', () => {
        forceListDisciplinesUpdate();
        dispatch(UploadActions.clearErrorMsg());
      }, 200);
    }
  }, [props?.checkUploadErrorMsg]);

  useEffect(() => {
    // Marcar novas notificações como 'vistas'
    if (
      !isLoading &&
      params?.constructionSiteId &&
      !isObjectEmpty(notificationsObj) &&
      obra?.Disciplines &&
      obra?.Disciplines?.length > 0
    ) {
      if (!canReadNow) {
        clear('canReadNow');
        set('canReadNow', () => {
          setCanReadNow(true);
        }, 600);
      } else {
        const disciplinesHasNotifications: INotificationInAppObra[] = [];
        const notificationAsView: INotificationMultipleReadRequest = {
          constructionSiteId: Number(params.constructionSiteId),
          notificationIds: [],
        };
        for (const notification of inAppNotifications) {
          if (notification.Folders.length > 0) {
            const lastFolderId = notification.Folders[notification.Folders.length - 1];
            if (lastFolderId === params.folderId) {
              notificationAsView.notificationIds.push(notification.NotificationId);
            } else {
              const findFolderIndex = notification.Folders.findIndex((folder) => folder === params.folderId);
              if (findFolderIndex !== -1) {
                const afterFolder = notification.Folders[findFolderIndex + 1];
                if (afterFolder) {
                  const findFileIndex = obra?.Disciplines.findIndex((discipline: any) => {
                    return discipline.DisciplineApiFolderId === afterFolder ||
                      `${discipline.ConstructionSiteDisciplinesId}` === afterFolder;
                  });
                  if (findFileIndex === -1) {
                    notificationAsView.notificationIds.push(notification.NotificationId);
                  }
                }
              }
            }
          }
          if (obra?.Disciplines.some((discipline: any) => {
            return discipline.DisciplineApiFolderId === notification.ItemId ||
              `${discipline.ConstructionSiteDisciplinesId}` === notification.ItemId;
          })) {
            disciplinesHasNotifications.push(notification);
          }
        }
        for (const disciplineNotification of disciplinesHasNotifications) {
          if (!notificationAsView.notificationIds.includes(disciplineNotification.NotificationId)) {
            notificationAsView.notificationIds.push(disciplineNotification.NotificationId);
          }
        }
        if (notificationAsView.notificationIds.length > 0) {
          addNotificationsToRead(
            `discipline-list-${params?.constructionSiteId}`,
            notificationAsView,
          );
        }
      }
    }
  }, [
    obra?.Disciplines,
    isLoading,
    notificationsObj,
    params?.constructionSiteId,
    addNotificationsToRead,
    canReadNow,
  ]);

  if (obra) {
    breadCrumbs[0] = {
      href: `/obras/detail/${obra.ConstructionSiteId}`,
      description: obra.Name,
    };
    breadCrumbs[1] = {
      href: `/obras/detail/${obra.ConstructionSiteId}`,
      description: t('breadcrumbFiles'),
    };
  } else {
    breadCrumbs[0] = {
      href: '',
      isLoading,
    };
    breadCrumbs[1] = {
      href: '',
      isLoading,
    };
  }

  return (
    <>
      <TabsShell
        ConstructionSiteId={params.constructionSiteId}
        active={PathActiveEnum.detail}
        loading={isLoading}
      />
      <PageHeader name={t('pageTitle')}>
        <BreadCrumb breadcrumbs={breadCrumbs} />
        <TableWrapper>
          {obra?.IsIntegrationAvailable && <TableActionTopbar buttons={(obra && getActionsToolbar()) || []} />}
          {isLoading ? (
            <LoadingDisciplinas multiple={6} />
          ) : (
            !obra?.IsIntegrationAvailable ?
              <ReconnectIntegrationBox
                integrationEmail={obra?.IntegrationEmail!}
                showReconnectButton={AccessPermission.isAdmin() || AccessPermission.isCoordenador()}
                csId={obra?.ConstructionSiteId!}
                api={obra?.Api!} />
              :
              <Table
                columns={columnsTable}
                dataSource={sortedDisciplines.map(
                  (discipline: any, index: number) => ({ ...discipline, key: index }),
                )}
                isSpaced={true}
                rowSelection={rowSelection}
              />
          )}
        </TableWrapper>

        <ContextMenu
          ref={contextMenuRef}
          id={menuId}
          menuHeight={followDisciplineFeature ? 176 : 127}
          menuWidth={208}
        >
          <MoreActionsDropdownContent
            preferencesFeature={followDisciplineFeature}
            record={contextMenuRecord}
            apiText={t('toolbar.viewIn', { api: ApisEnumLabel[obra?.Api || 0] })}
            onToggleFollow={toggleFollowDiscipline}
            onEdit={editDiscipline}
            onOpenSA={handleOpenSA}
            onOpenHistoryDrawer={() => undefined}
            onOpenModalExclusao={openModalExcluirDisciplina}
          />
        </ContextMenu>
      </PageHeader>
      {showCadastroDisciplina && (
        <CadastroDisciplina
          constructionSiteId={obra?.ConstructionSiteId || 0}
          id={editedDisciplineId}
          description={editedDisciplineName}
          visible={showCadastroDisciplina}
          onCancel={closeModalDiscipline}
        />
      )}
      <ExclusaoDisciplinas
        isLoading={isLoading}
        folderId={editedFolderId}
        id={editedDisciplineId}
        icon={editedDisciplineIcon}
        constructionSiteId={obra?.ConstructionSiteId || 0}
        name={editedDisciplineName}
        visible={showModalExclusaoDisciplina}
        onCancel={() => setShowModalExclusaoDisciplina(false)}
        onConfirmDelete={removeFiles}
        onConfirmOpenDiscipline={openDisciplineOnCloud}
      />
      {showSolutionSAConnection && disciplineForSolution && (
        <SolutionSAConnection
          platform={obra?.Api || 0}
          visible={showSolutionSAConnection}
          discipline={disciplineForSolution}
          onCancel={closeSoluctionSAConnection}
        />
      )}
      {showSameAccountErrorModal && obra && (
        <SameAccountErrorModal
          visible={showSameAccountErrorModal}
          integrationEmail={obra.IntegrationEmail}
          csId={obra.ConstructionSiteId}
          api={obra.Api}
          onCancel={() => setShowSameAccountErrorModal(false)}
        />
      )}
    </>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  currentUserPrivileges: state.listagemArquivos.currentUserPrivileges,
  userInfo: state.auth.userInfo,
  checkUploadErrorMsg: state.upload.error,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(Disciplinas);
